import React, { useEffect, useState } from 'react';
import { View, Text, Modal, TouchableOpacity, StyleSheet, ScrollView, Alert, ActivityIndicator, Platform } from 'react-native';
import AppTextInput from './AppTextInput';
import UpgradeRequiredModal from './UpgradeRequiredModal';
import type { Appointment, OrderLineItem } from '../utils/appointmentManager';

interface RequestPaymentModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSent: () => void;
  onNavigateToPackageSelection?: () => void;
}

const MONTHLY_LIMITS: Record<string, number> = {
  starter: 15,
  professional: 50,
  business: 100,
};

function lineItemTotal(item: { unitPrice: number; quantity: number }): number {
  return item.unitPrice * item.quantity;
}

function startOfCurrentMonthIso(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
}

/** Real, single "Request Payment" flow — replaces the previously-fake SMS
 * payment request and Send Invoice features. Sends a real SMS with an
 * itemized breakdown + a working (no-login) payment link. Gated to
 * Starter+ plans, with a monthly send cap per plan to bound SMS cost. */
const RequestPaymentModal: React.FC<RequestPaymentModalProps> = ({
  visible,
  appointment,
  onClose,
  onSent,
  onNavigateToPackageSelection,
}) => {
  const [checking, setChecking] = useState(true);
  const [gate, setGate] = useState<{ blocked: boolean; title: string; message: string } | null>(null);
  const [items, setItems] = useState<Array<{ description: string; amount: string }>>([{ description: '', amount: '' }]);
  const [sending, setSending] = useState(false);
  const [businessOwnerId, setBusinessOwnerId] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !appointment) return;
    setChecking(true);
    setGate(null);

    (async () => {
      try {
        const { AuthManager } = await import('../utils/authManager');
        const owner = await AuthManager.getSavedBusinessOwner();
        if (!owner) {
          setGate({ blocked: true, title: 'Error', message: 'Could not load your business account. Please try again.' });
          setChecking(false);
          return;
        }
        setBusinessOwnerId(owner.id);

        const { default: productSubscriptionService } = await import('../services/productSubscriptionService');
        const status = await productSubscriptionService.getStatus(owner.id);
        const planId = status.planId || 'free';

        if (planId === 'free') {
          setGate({
            blocked: true,
            title: 'Upgrade Required',
            message: 'Requesting payment via SMS is available on the Starter plan and above. Upgrade to send real payment links to your customers.',
          });
          setChecking(false);
          return;
        }

        const limit = MONTHLY_LIMITS[planId] ?? MONTHLY_LIMITS.starter;
        const sentThisMonth = await countPaymentRequestsThisMonth(owner.id);
        if (sentThisMonth >= limit) {
          setGate({
            blocked: true,
            title: 'Monthly Limit Reached',
            message: `You've used all ${limit} payment requests included in your plan this month. Upgrade for a higher monthly limit.`,
          });
          setChecking(false);
          return;
        }

        // Pre-fill from structured order items when available; otherwise a
        // blank manual line for older/service-only bookings.
        if (appointment.orderLineItems && appointment.orderLineItems.length > 0) {
          setItems(
            appointment.orderLineItems.map((item) => ({
              description: item.name,
              amount: lineItemTotal(item).toFixed(2),
            }))
          );
        } else {
          const existingAmount = Number(appointment.paymentData?.amount) || 0;
          setItems([{ description: appointment.serviceType || 'Service', amount: existingAmount > 0 ? existingAmount.toFixed(2) : '' }]);
        }

        setGate(null);
      } catch (error) {
        console.error('RequestPaymentModal gate check failed:', error);
        setGate({ blocked: true, title: 'Error', message: 'Could not check your plan. Please try again.' });
      } finally {
        setChecking(false);
      }
    })();
  }, [visible, appointment]);

  async function countPaymentRequestsThisMonth(ownerId: string): Promise<number> {
    if (Platform.OS !== 'web') return 0;
    try {
      const { collection, query, where, getDocs } = require('firebase/firestore');
      const { getWebDb } = require('../services/unifiedAuthService');
      const db = getWebDb();
      if (!db) return 0;
      const q = query(
        collection(db, 'paymentRequestSends'),
        where('businessOwnerId', '==', ownerId),
        where('sentAt', '>=', startOfCurrentMonthIso())
      );
      const snap = await getDocs(q);
      return snap.size;
    } catch (error) {
      console.warn('countPaymentRequestsThisMonth failed, failing open:', error);
      return 0;
    }
  }

  if (!visible || !appointment) {
    return null;
  }

  if (gate?.blocked) {
    return (
      <UpgradeRequiredModal
        visible={visible}
        title={gate.title}
        message={gate.message}
        onDismiss={onClose}
        onUpgrade={() => {
          onClose();
          onNavigateToPackageSelection?.();
        }}
      />
    );
  }

  const total = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  const updateItem = (index: number, field: 'description' | 'amount', value: string) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const addLine = () => setItems((prev) => [...prev, { description: '', amount: '' }]);
  const removeLine = (index: number) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleSend = async () => {
    const validItems = items.filter((item) => item.description.trim() && Number(item.amount) > 0);
    if (validItems.length === 0) {
      Alert.alert('Missing details', 'Add at least one line item with a description and amount.');
      return;
    }
    if (!appointment.phone) {
      Alert.alert('No phone number', 'This booking has no customer phone number on file.');
      return;
    }
    if (!businessOwnerId) {
      Alert.alert('Error', 'Could not identify your business. Please try again.');
      return;
    }

    setSending(true);
    try {
      const lineItems: OrderLineItem[] = validItems.map((item, i) => ({
        itemId: `manual_${i}`,
        name: item.description.trim(),
        quantity: 1,
        unitPrice: Number(item.amount),
      }));
      const finalTotal = lineItems.reduce((sum, item) => sum + lineItemTotal(item), 0);

      const { appointmentManager } = await import('../utils/appointmentManager');
      await appointmentManager.updateAppointmentOrderItems(appointment.id, lineItems, {
        amount: finalTotal,
        status: 'pending',
        requestedAt: new Date().toISOString(),
      });

      const payLink = `https://bookapt.io/app.html?payLink=1&appointmentId=${encodeURIComponent(appointment.id)}`;
      const itemsSummary = validItems.map((item) => `${item.description}: $${Number(item.amount).toFixed(2)}`).join(', ');
      const message = `${appointment.businessName || 'Your business'} sent you a payment request: ${itemsSummary}. Total: $${finalTotal.toFixed(2)}. Pay here: ${payLink}`;

      const { smsService } = await import('../services/smsService');
      const smsSent = await smsService.sendSMS(appointment.phone, message, 'general');
      if (!smsSent) {
        Alert.alert('Could not send', 'The payment request could not be sent by SMS. Please try again.');
        return;
      }

      // Log the send for the monthly cost-control cap.
      try {
        const { collection, addDoc, serverTimestamp } = require('firebase/firestore');
        const { getWebDb } = require('../services/unifiedAuthService');
        const db = getWebDb();
        if (db) {
          await addDoc(collection(db, 'paymentRequestSends'), {
            businessOwnerId,
            appointmentId: appointment.id,
            sentAt: new Date().toISOString(),
            createdAt: serverTimestamp(),
          });
        }
      } catch (logError) {
        console.warn('Could not log payment request send (non-fatal):', logError);
      }

      Alert.alert('Sent', 'Payment request sent to the customer.');
      onSent();
      onClose();
    } catch (error: any) {
      console.error('Error sending payment request:', error);
      Alert.alert('Error', error?.message || 'Could not send payment request.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Request Payment</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          </View>

          {checking ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator />
            </View>
          ) : (
            <>
              <ScrollView style={styles.content}>
                <Text style={styles.customerLine}>To: {appointment.customerName} ({appointment.phone})</Text>
                {items.map((item, index) => (
                  <View key={index} style={styles.itemRow}>
                    <AppTextInput
                      style={[styles.input, styles.descInput]}
                      placeholder="Description"
                      value={item.description}
                      onChangeText={(v) => updateItem(index, 'description', v)}
                    />
                    <AppTextInput
                      style={[styles.input, styles.amountInput]}
                      placeholder="$0.00"
                      value={item.amount}
                      onChangeText={(v) => updateItem(index, 'amount', v)}
                      keyboardType="decimal-pad"
                    />
                    {items.length > 1 && (
                      <TouchableOpacity onPress={() => removeLine(index)}>
                        <Text style={styles.removeText}>✕</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ))}
                <TouchableOpacity onPress={addLine} style={styles.addLineButton}>
                  <Text style={styles.addLineText}>+ Add line item</Text>
                </TouchableOpacity>

                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Total:</Text>
                  <Text style={styles.totalValue}>${total.toFixed(2)}</Text>
                </View>
              </ScrollView>

              <View style={styles.footer}>
                <TouchableOpacity style={styles.cancelButton} onPress={onClose} disabled={sending}>
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.sendButton, sending && styles.sendButtonDisabled]} onPress={handleSend} disabled={sending}>
                  {sending ? <ActivityIndicator color="#fff" /> : <Text style={styles.sendButtonText}>Send Payment Request</Text>}
                </TouchableOpacity>
              </View>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 20,
    width: '95%',
    maxWidth: 600,
    maxHeight: '85%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: '#666',
  },
  loadingWrap: {
    padding: 40,
    alignItems: 'center',
  },
  content: {
    padding: 16,
  },
  customerLine: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 10,
    fontSize: 15,
  },
  descInput: {
    flex: 1,
  },
  amountInput: {
    width: 90,
  },
  removeText: {
    fontSize: 16,
    color: '#c00',
    paddingHorizontal: 4,
  },
  addLineButton: {
    marginTop: 4,
    marginBottom: 16,
  },
  addLineText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  totalLabel: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  totalValue: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 10,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
  },
  sendButton: {
    flex: 1,
    paddingVertical: 12,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    alignItems: 'center',
  },
  sendButtonDisabled: {
    opacity: 0.6,
  },
  sendButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
  },
});

export default RequestPaymentModal;
